import {
  ControlTypeEnum,
  FieldsEnum,
  ModelPathEnum,
  SectionsEnum,
} from 'app/shared/enums';
import { FormConfig } from 'app/shared/interfaces/FormConfig.interface';
import { FormSectionConfigInterface } from 'app/shared/interfaces/FormSectionConfig.interface';
import { QuestionInterface } from 'app/shared/interfaces/Question.interface';
import { DictService } from 'app/services/dict.service';
import cloneDeep from 'lodash-es/cloneDeep';

export interface AddConsentsOptions {
  sectionKey: SectionsEnum;
  sectionLabel?: string;
  regulationsConsentField?: FieldsEnum;
  marketingConsentField?: FieldsEnum;
  regulationsModelPath?: string;
  marketingModelPath?: string;
  regulationsOnChange?: string[];
  marketingOnChange?: string[];
  shouldPatch?: boolean;
  clearOnHide?: boolean;
  regulationsRequired?: boolean;
  marketingRequired?: boolean;
}

export async function addConsentsToFormConfig(
  config: FormConfig,
  options: AddConsentsOptions
): Promise<FormConfig> {
  const {
    sectionKey,
    sectionLabel = '',
    regulationsConsentField = FieldsEnum.ConsentRegulations,
    marketingConsentField = FieldsEnum.ConsentMarketing,
    regulationsModelPath = ModelPathEnum.ConsentsRegulationsConsent,
    marketingModelPath = ModelPathEnum.ConsentsMarketingConsent,
    regulationsOnChange = [],
    marketingOnChange = [],
    shouldPatch = false,
    clearOnHide = true,
    regulationsRequired = true,
    marketingRequired = false,
  } = options;

  if (!config.screens || config.screens.length === 0) {
    return config;
  }

  let regulationsLabel = '';
  let marketingLabel = '';

  try {
    const consents = await DictService.getDictionary('consents');
    const regulationsConsent = consents.data.find(
      (consent) => consent.key === 'regulations'
    );
    const marketingConsent = consents.data.find(
      (consent) => consent.key === 'marketing'
    );

    if (regulationsConsent) {
      regulationsLabel = regulationsConsent.value || '';
    }
    if (marketingConsent) {
      marketingLabel = marketingConsent.value || '';
    }
  } catch (error) {
    console.error('Error fetching consents:', error);
  }

  const clonedConfig = cloneDeep(config);
  const lastScreen = clonedConfig.screens[clonedConfig.screens.length - 1];
  if (!lastScreen.sections) {
    lastScreen.sections = [];
  }

  const existingSectionIndex = lastScreen.sections.findIndex(
    (section) => section.key === sectionKey
  );

  const regulationsQuestion: QuestionInterface = {
    key: regulationsConsentField,
    modelPath: regulationsModelPath,
    label: regulationsLabel,
    type: ControlTypeEnum.Checkbox,
    required: regulationsRequired,
    visible: true,
    editable: true,
    ignoreAnonymized: true,
    shouldPatch,
    clearOnHide,
    width: 'full',
    onChange: regulationsOnChange.length > 0 ? regulationsOnChange : undefined,
  };

  const marketingQuestion: QuestionInterface = {
    key: marketingConsentField,
    modelPath: marketingModelPath,
    label: marketingLabel,
    type: ControlTypeEnum.Checkbox,
    required: marketingRequired,
    visible: true,
    editable: true,
    ignoreAnonymized: true,
    shouldPatch,
    clearOnHide,
    width: 'full',
    onChange: marketingOnChange.length > 0 ? marketingOnChange : undefined,
  };

  const consentsSection: FormSectionConfigInterface = {
    key: sectionKey,
    label: sectionLabel,
    subLabel: '',
    visible: true,
    questions: [regulationsQuestion, marketingQuestion],
  };

  if (existingSectionIndex >= 0) {
    lastScreen.sections[existingSectionIndex] = consentsSection;
  } else {
    lastScreen.sections.push(consentsSection);
  }

  return clonedConfig;
}
